// src/components/InteractiveTutorial.jsx
// Action-driven interactive tutorial — advances when the user performs each step
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const FL = "'Manrope', -apple-system, sans-serif";
const FH = "'Noto Serif', Georgia, serif";

// ── Spotlight overlay + tooltip ──────────────────────────────────
function Spotlight({ target, fallbackTarget, tooltip, placement, onSkip, onBack, showNext, onNext, progress, skipLabel, backLabel, nextLabel, finishLabel, isLast, canGoBack, hidden }) {
  const [rect, setRect] = useState(null);
  const [activeTarget, setActiveTarget] = useState(target);
  const PAD = 8;

  useEffect(() => {
    if (!target || hidden) { setRect(null); setActiveTarget(null); return; }
    const resolve = () => {
      const el = typeof target === "string" ? document.querySelector(target) : target;
      if (el) return el;
      if (fallbackTarget) {
        return typeof fallbackTarget === "string" ? document.querySelector(fallbackTarget) : fallbackTarget;
      }
      return null;
    };
    const update = () => {
      const el = resolve();
      if (!el) { setRect(null); setActiveTarget(null); return; }
      setActiveTarget(typeof target === "string" && document.querySelector(target) ? target : fallbackTarget);
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    update();
    // Poll for dynamically appearing elements
    const poll = setInterval(update, 500);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearInterval(poll); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [target, fallbackTarget, hidden]);

  if (hidden) return null;

  const centered = !target;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      {/* Dark overlay with cutout */}
      {centered ? (
        <div style={{ position: "absolute", inset: 0, background: "rgba(47,52,48,0.5)", pointerEvents: "auto" }} />
      ) : rect ? (
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
          <defs>
            <mask id="tutorial-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect x={rect.left} y={rect.top} width={rect.width} height={rect.height} rx="12" ry="12" fill="black" />
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(47,52,48,0.5)" mask="url(#tutorial-mask)" />
        </svg>
      ) : (
        <div style={{ position: "absolute", inset: 0, background: "rgba(47,52,48,0.5)", pointerEvents: "auto" }} />
      )}

      {/* Tooltip */}
      <div style={{
        position: placement === "fixed-bottom" ? "fixed" : "absolute",
        zIndex: 10002, pointerEvents: "auto",
        ...(centered
          ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
          : placement === "fixed-bottom"
            ? getFixedBottomPosition(rect)
            : getTooltipPosition(rect, placement)),
        background: "#fff", borderRadius: 14, padding: "22px 24px",
        boxShadow: "0 8px 40px rgba(47,52,48,0.2)",
        maxWidth: 320, width: centered ? 300 : undefined,
        animation: "tutorialFadeIn 0.25s ease",
      }}>
        {tooltip.title && (
          <p style={{ margin: "0 0 6px", fontFamily: FH, fontStyle: "italic", fontSize: 17, fontWeight: 700, color: "#2f3430" }}>
            {tooltip.title}
          </p>
        )}
        <p style={{ margin: 0, fontFamily: FL, fontSize: 13, lineHeight: 1.65, color: "#5c605c", whiteSpace: "pre-line" }}>
          {tooltip.content}
        </p>
        {tooltip.hint && (
          <p style={{
            margin: "10px 0 0", padding: "8px 10px", borderRadius: 8,
            background: "#f4f4f0", fontFamily: FL, fontSize: 11, color: "#8a8e8a",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>touch_app</span>
            {tooltip.hint}
          </p>
        )}

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {canGoBack ? (
              <button onClick={onBack} style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontFamily: FL, fontSize: 11, color: "#8a8e8a",
                display: "flex", alignItems: "center", gap: 2,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_back</span>
                {backLabel}
              </button>
            ) : (
              <button onClick={onSkip} style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                fontFamily: FL, fontSize: 11, color: "#8a8e8a",
              }}>
                {skipLabel}
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {progress && <span style={{ fontFamily: FL, fontSize: 11, color: "#c7c4bf" }}>{progress}</span>}
            {showNext && (
              <button onClick={onNext} style={{
                background: "#655d54", color: "#fff6ef", border: "none",
                borderRadius: 8, padding: "9px 20px", cursor: "pointer",
                fontFamily: FL, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
              }}>
                {isLast ? finishLabel : nextLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tutorialFadeIn {
          from { opacity: 0; transform: ${centered ? "translate(-50%, -50%) scale(0.95)" : "translateY(6px)"}; }
          to { opacity: 1; transform: ${centered ? "translate(-50%, -50%) scale(1)" : "translateY(0)"}; }
        }
      `}</style>
    </div>
  );
}

function getTooltipPosition(rect, placement = "bottom") {
  if (!rect) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw <= 768;
  const margin = 12;
  const mw = Math.min(320, vw - 32);

  // If target is in the bottom half and placement is "bottom", flip to "top"
  // If target is in the top quarter and placement is "top", flip to "bottom"
  let p = placement;
  if (p === "bottom" && rect.top + rect.height + margin + 200 > vh) p = "top";
  if (p === "top" && rect.top - margin - 200 < 0) p = "bottom";

  switch (p) {
    case "top":
      return { bottom: vh - rect.top + margin, left: Math.max(16, Math.min(rect.left + rect.width / 2 - 160, vw - mw - 16)), maxWidth: mw };
    case "right": {
      const tooltipTop = rect.top;
      // If target is in bottom third, anchor tooltip to bottom instead
      if (tooltipTop + 200 > vh) {
        return { bottom: Math.max(16, vh - rect.top - rect.height), left: Math.min(rect.left + rect.width + margin, vw - mw - 16), maxWidth: mw };
      }
      return { top: Math.max(16, tooltipTop), left: Math.min(rect.left + rect.width + margin, vw - mw - 16), maxWidth: mw };
    }
    case "left":
      return { top: Math.max(16, rect.top), right: Math.max(16, vw - rect.left + margin), maxWidth: mw };
    case "bottom":
    default:
      return { top: rect.top + rect.height + margin, left: Math.max(16, isMobile ? 16 : Math.min(rect.left, vw - mw - 16)), maxWidth: mw };
  }
}

function getFixedBottomPosition(rect) {
  const vw = window.innerWidth;
  const isMobile = vw <= 768;
  const mw = Math.min(320, vw - 32);
  // If we have a target rect, position directly below it
  if (rect) {
    return {
      top: rect.top + rect.height + 12,
      left: Math.max(16, rect.left),
      maxWidth: mw,
    };
  }
  return {
    bottom: isMobile ? 80 : 24,
    left: isMobile ? 16 : "50%",
    transform: isMobile ? "none" : "translateX(-50%)",
    maxWidth: mw,
  };
}

// ── Step definitions ─────────────────────────────────────────────
// showNext: true = Next button, false = action-driven
// hidden: step is active but overlay is hidden (waiting for async action like save)
export function getTutorialSteps(t, isMobile) {
  return [
    // 0 — Welcome
    { target: null, showNext: true, tooltip: {
      title: t("tutorial.welcome_title"),
      content: t("tutorial.welcome_desc"),
    }},
    // 1 — Tap search tab
    { target: '[data-tutorial="tab-search"]', placement: isMobile ? "top" : "right", tooltip: {
      title: t("tutorial.search_tab_title"),
      content: t("tutorial.search_tab_desc"),
      hint: t("tutorial.hint_tap_search"),
    }},
    // 2 — Search for a place
    { target: '[data-tutorial="search-input"]', placement: isMobile ? "fixed-bottom" : "bottom", tooltip: {
      title: t("tutorial.search_input_title"),
      content: t("tutorial.search_input_desc"),
      hint: t("tutorial.hint_search"),
    }},
    // 3 — Tap "Add to my places" on first result
    { target: '[data-tutorial="add-place-btn"]', placement: "fixed-bottom", tooltip: {
      title: t("tutorial.add_place_title"),
      content: t("tutorial.add_place_desc"),
      hint: t("tutorial.hint_add_place"),
    }},
    // 4 — SavePlaceModal is open → hide tutorial, wait for save
    { target: null, hidden: true },
    // 5 — Place saved! Best picks info + profile management
    { target: null, showNext: true, tooltip: {
      title: t("tutorial.saved_best_title"),
      content: t("tutorial.saved_best_desc"),
    }},
    // 6 — Tap people tab
    { target: '[data-tutorial="search-mode-person"]', placement: "top", tooltip: {
      title: t("tutorial.people_title"),
      content: t("tutorial.people_desc"),
      hint: t("tutorial.hint_people"),
    }},
    // 7 — Search & follow (type "taemin")
    { target: '[data-tutorial="follow-btn"]', fallbackTarget: '[data-tutorial="search-input"]', placement: "bottom", tooltip: {
      title: t("tutorial.follow_title"),
      content: t("tutorial.follow_desc"),
      hint: t("tutorial.hint_follow"),
    }},
    // 8 — Go to Home (map) tab first
    { target: '[data-tutorial="tab-map"]', placement: isMobile ? "top" : "right", tooltip: {
      title: t("tutorial.go_home_title"),
      content: t("tutorial.go_home_desc"),
      hint: t("tutorial.hint_go_home"),
    }},
    // 9 — Our Space (tap Our Curation toggle)
    { target: '[data-tutorial="map-mode-toggle"]', placement: "bottom", tooltip: {
      title: t("tutorial.our_space_title"),
      content: t("tutorial.our_space_desc"),
      hint: t("tutorial.hint_our_space"),
    }},
    // 10 — Profile tab
    { target: '[data-tutorial="tab-profile"]', placement: isMobile ? "top" : "right", tooltip: {
      title: t("tutorial.profile_title"),
      content: t("tutorial.profile_desc"),
      hint: t("tutorial.hint_profile"),
    }},
    // 11 — Done
    { target: null, showNext: true, tooltip: {
      title: t("tutorial.done_title"),
      content: t("tutorial.done_desc"),
    }},
  ];
}

// ── Main component ───────────────────────────────────────────────
export default function InteractiveTutorial({ step, onSkip, onNext, onBack }) {
  const { t } = useTranslation();
  const isMobile = window.innerWidth <= 768;

  if (step == null) return null;

  const STEPS = getTutorialSteps(t, isMobile);
  const current = STEPS[step];
  if (!current) return null;

  return (
    <Spotlight
      target={current.target}
      fallbackTarget={current.fallbackTarget}
      tooltip={current.tooltip || {}}
      placement={current.placement}
      showNext={current.showNext || false}
      hidden={current.hidden || false}
      onSkip={onSkip}
      onBack={onBack}
      onNext={onNext}
      canGoBack={step > 0}
      progress={`${step + 1} / ${STEPS.length}`}
      skipLabel={t("tutorial.skip")}
      backLabel={t("tutorial.back")}
      nextLabel={t("tutorial.next")}
      finishLabel={t("tutorial.finish")}
      isLast={step === STEPS.length - 1}
    />
  );
}
